import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { contentService } from '@/services/content';
import { horoscopoApi, type HoroscopeTemplate } from '@/services/horoscopo';

import styles from './ConfigHoroscopo.module.css';

/**
 * Aviso honesto da casa. Estilo inline (só tokens, sem cor literal) porque o
 * módulo CSS desta tela não tem classe de aviso — mesmo padrão de
 * ConfigQuentes.tsx.
 */
const AVISO: React.CSSProperties = {
  margin: '0 0 var(--sp-3)',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-2)',
  borderLeft: '2px solid var(--warn)',
  borderRadius: 'var(--r-sm)',
  color: 'var(--text-3)',
  fontSize: 'var(--fs-xs)',
  lineHeight: 'var(--lh-normal)',
  maxWidth: '78ch',
};

export function ConfigHoroscopo() {
  const qc = useQueryClient();
  const [tpls, setTpls] = useState<HoroscopeTemplate[] | null>(null);
  const [prompt, setPrompt] = useState<string>('');

  const { data } = useQuery({
    queryKey: ['horoscopo', 'templates'],
    queryFn: horoscopoApi.getTemplates,
  });
  const promptQuery = useQuery({
    queryKey: ['horoscopo', 'prompt'],
    queryFn: contentService.getHoroscopePrompt,
  });

  useEffect(() => {
    if (data) setTpls(structuredClone(data.templates).sort((a, b) => a.ordem - b.ordem));
  }, [data]);
  useEffect(() => {
    if (promptQuery.data) setPrompt(promptQuery.data.template);
  }, [promptQuery.data]);

  const save = useMutation({
    mutationFn: (list: HoroscopeTemplate[]) => horoscopoApi.saveTemplates(list),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horoscopo', 'templates'] }),
  });
  const savePrompt = useMutation({
    mutationFn: (t: string) => contentService.setHoroscopePrompt(t),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horoscopo', 'prompt'] }),
  });

  if (!tpls) {
    return (
      <section className={styles.root}>
        <p className={styles.muted}>Carregando…</p>
      </section>
    );
  }

  const patch = (i: number, field: keyof HoroscopeTemplate, value: string | number) =>
    setTpls(tpls.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Templates do Horóscopo</h1>
          <p className={styles.subtitle}>
            O padrão diário de publicações. Cada template define um tipo do dia: o rótulo, a
            expressão que casa o título no WordPress, e a estrutura-padrão do conteúdo.
          </p>
        </div>
        <Button onClick={() => save.mutate(tpls)} disabled={save.isPending}>
          {save.isPending ? 'A gravar…' : 'Salvar'}
        </Button>
      </header>

      {save.isSuccess && <p className={styles.ok}>Templates gravados.</p>}

      {/* Prompt de geração (SEO h3 + PAA) — editável; {SIGNO} vira o signo.
          O campo grava, mas quem lê 'horoscope_prompt' é _write_horoscope
          (content_engine.py:1205), que não tem chamador: o caminho vivo é
          horoscopo_engine, que monta o prompt internamente e resolve papéis
          pelo fluxo de Engenharia. O campo fica; a promessa não. */}
      <div className={styles.promptCard}>
        <p role="status" style={AVISO}>
          Este prompt é do motor antigo e NÃO é lido pelo motor atual (Engenharia → fluxo Horóscopo
          é o vivo).
        </p>
        <div className={styles.promptHead}>
          <div>
            <h2 className={styles.promptTitle}>Prompt de geração</h2>
            <p className={styles.promptHint}>
              Define como a IA escreve cada signo (SEO, h3 cobrindo as PAA). Use{' '}
              <code>{'{SIGNO}'}</code> onde quiser o nome do signo. A regra de saída JSON é
              garantida automaticamente.
            </p>
          </div>
          <div className={styles.promptActions}>
            <Button variant="ghost" onClick={() => setPrompt(promptQuery.data?.default ?? '')}>
              Restaurar padrão
            </Button>
            <Button onClick={() => savePrompt.mutate(prompt)} disabled={savePrompt.isPending}>
              {savePrompt.isPending ? 'A gravar…' : 'Salvar prompt'}
            </Button>
          </div>
        </div>
        <textarea
          className={styles.promptArea}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={14}
          spellCheck={false}
        />
        {savePrompt.isSuccess && <p className={styles.ok}>Prompt gravado.</p>}
      </div>

      <div className={styles.list}>
        {tpls.map((t, i) => (
          <div key={t.key} className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.ordem}>{t.ordem}</span>
              <input
                className={styles.labelInput}
                value={t.label}
                onChange={(e) => patch(i, 'label', e.target.value)}
              />
              <span className={styles.key}>{t.key}</span>
            </div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Casa o título (regex)</span>
              <input
                className={styles.input}
                value={t.match}
                onChange={(e) => patch(i, 'match', e.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Estrutura-padrão</span>
              <textarea
                className={styles.textarea}
                rows={2}
                value={t.estrutura}
                onChange={(e) => patch(i, 'estrutura', e.target.value)}
              />
            </label>
          </div>
        ))}
      </div>
    </section>
  );
}
